import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { projectId } from '../utils/supabase/info';
import { AuthButton } from './AuthButton';

export function OAuthQuickTest() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  // Auto-run the test on mount
  useEffect(() => {
    runTest();
  }, []);

  async function runTest() {
    setTesting(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/public/oauth/test`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to test OAuth configuration');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('OAuth test error:', err);
      setError(err instanceof Error ? err.message : 'Failed to test OAuth');
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-end mb-4">
          <AuthButton />
        </div>
        <div className="text-center mb-8">
          <h1 className="text-3xl text-gray-900 mb-2">Google OAuth Configuration Test</h1>
          <p className="text-gray-600">Testing your Google Calendar integration credentials</p>
        </div>

        {testing && (
          <Card className="p-8 text-center">
            <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg text-gray-700">Testing OAuth configuration...</p>
          </Card>
        )}

        {error && (
          <Card className="p-6 border-2 border-red-500 bg-red-50">
            <div className="flex items-start">
              <XCircle className="w-6 h-6 text-red-600 mr-3 shrink-0 mt-1" />
              <div>
                <h3 className="text-lg text-red-900 mb-2">Test Failed</h3>
                <p className="text-red-700">{error}</p>
                <Button
                  onClick={runTest}
                  className="mt-4"
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </Card>
        )}

        {result && !testing && (
          <div className="space-y-6">
            {/* Overall Status */}
            <Card className={`p-6 border-2 ${
              result.status === 'OK' 
                ? 'border-green-500 bg-green-50' 
                : result.status === 'WARNING'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-red-500 bg-red-50'
            }`}>
              <div className="flex items-start">
                {result.status === 'OK' ? (
                  <CheckCircle className="w-8 h-8 text-green-600 mr-4 shrink-0" />
                ) : result.status === 'WARNING' ? (
                  <AlertCircle className="w-8 h-8 text-yellow-600 mr-4 shrink-0" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600 mr-4 shrink-0" />
                )}
                <div className="flex-1">
                  <h2 className="text-2xl text-gray-900 mb-2">
                    {result.status === 'OK' ? 'Configuration Looks Good' : 
                     result.status === 'WARNING' ? 'Configuration Has Warnings' :
                     'Configuration Has Errors'}
                  </h2>
                  {result.message && (
                    <p className="text-gray-700">{result.message}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Credential Checks */}
            <Card className="p-6">
              <h3 className="text-xl text-gray-900 mb-4">Credential Status</h3>
              <div className="space-y-4">
                {/* Client ID */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-sm text-gray-500 mr-2">GOOGLE_OAUTH_CLIENT_ID</span>
                      {result.checks?.clientIdSet ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    {result.checks?.clientIdFormat ? (
                      <div className="flex items-center text-sm text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Correct format (ends with .apps.googleusercontent.com)
                      </div>
                    ) : (
                      <div className="flex items-center text-sm text-red-700">
                        <XCircle className="w-3 h-3 mr-1" />
                        Invalid format - should end with .apps.googleusercontent.com
                      </div>
                    )}
                    {result.checks?.clientIdPreview && (
                      <div className="mt-2">
                        <code className="text-xs text-gray-600 break-all">
                          {result.checks.clientIdPreview}
                        </code>
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Secret */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-sm text-gray-500 mr-2">GOOGLE_OAUTH_CLIENT_SECRET</span>
                      {result.checks?.clientSecretSet ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    {result.checks?.clientSecretLength && (
                      <div className="flex items-center text-sm text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Appears to be correct length
                      </div>
                    )}
                    {result.checks?.credentialsMismatch && (
                      <div className="flex items-center text-sm text-red-700 mt-2">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Warning: Client ID and Secret look very similar — verify they are different.
                      </div>
                    )}
                  </div>
                </div>

                {/* Supabase URL */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-sm text-gray-500 mr-2">SUPABASE_URL</span>
                      {result.checks?.supabaseUrlSet ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    {result.checks?.supabaseUrl && (
                      <code className="text-xs text-gray-600">
                        {result.checks.supabaseUrl}
                      </code>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Redirect URI */}
            {result.checks?.redirectUri && (
              <Card className="p-6 border-2 border-blue-500">
                <h3 className="text-xl text-gray-900 mb-3 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Important: Add This Redirect URI to Google Cloud Console
                </h3>
                <p className="text-gray-600 mb-4">
                  Copy this EXACT redirect URI and add it to your OAuth credentials in Google Cloud Console:
                </p>
                <div className="bg-gray-900 p-4 rounded-lg mb-4">
                  <code className="text-sm text-green-400 break-all">
                    {result.checks.redirectUri}
                  </code>
                </div>
                <a
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:underline"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Open Google Cloud Console
                </a>
              </Card>
            )}

            {/* Instructions */}
            {result.instructions && result.instructions.length > 0 && (
              <Card className="p-6 bg-blue-50">
                <h3 className="text-xl text-gray-900 mb-4">Next Steps</h3>
                <ol className="space-y-2 list-decimal list-inside">
                  {result.instructions.map((instruction: string, idx: number) => (
                    <li key={idx} className="text-gray-700">
                      {instruction}
                    </li>
                  ))}
                </ol>
              </Card>
            )}

            {/* Success - Next Steps */}
            {result.status === 'OK' && (
              <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500">
                <h3 className="text-xl text-gray-900 mb-4">What's Next</h3>
                <div className="space-y-3 text-gray-700">
                  <p>Your OAuth credentials are configured correctly! You can now:</p>
                  <ol className="space-y-2 list-decimal list-inside ml-4">
                    <li>Log into PathKit as a System Administrator</li>
                    <li>Navigate to System Administration → Calendar</li>
                    <li>Add a BD Rep and connect their Google Calendar</li>
                    <li>Configure booking settings (duration, hours, etc.)</li>
                    <li>Test by booking a demo on your public /book-demo page</li>
                  </ol>
                </div>
              </Card>
            )}

            {/* Test Again Button */}
            <div className="text-center">
              <Button
                onClick={runTest}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Run Test Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}